/**
 * Centralized client-screen copy module.
 *
 * Punctuation convention:
 *   - Titles and headings carry NO trailing period.
 *   - Descriptions are sentence-style and end with a period uniformly.
 *   - Multi-clause descriptions use a comma or period — never a semicolon mid-list.
 */

import type { CommunicationMode } from '../types/app';

// ---------------------------------------------------------------------------
// ArrivalScreen
// ---------------------------------------------------------------------------

export const arrivalCopy = {
  sectionLabel: 'Your Private Consultation',
  welcomeHeading: 'Welcome to your personal consultation.',
  welcomeBody: 'Our specialist is delighted to assist you today.',
  cta: 'Continue',
} as const;

// ---------------------------------------------------------------------------
// PreferencesScreen
// ---------------------------------------------------------------------------

export const preferencesCopy = {
  heading: 'How would you prefer to connect?',
  subHeading: 'Select your comfort level',
  cards: {
    video: {
      title: 'Face-to-face conversation',
      description: 'You and your specialist will be on camera.',
    },
    audio: {
      title: 'Voice-only conversation',
      description: 'You will see your specialist, and your camera stays off.',
    },
    chat: {
      title: 'Begin with messages',
      description: 'Switch to video or audio at any time.',
    },
  },
  helperText: 'You may change your preference at any point during the consultation.',
  cta: 'Begin Consultation',
  // W5 rule 1, this side of it: a customer arriving after the advisor should not
  // have to guess whether anyone is there. Worded as a fact, not a recommendation —
  // the choice stays entirely the customer's, and nothing here preselects a card.
  advisorPresence: {
    video: 'Your advisor is already here, on camera.',
    audio: 'Your advisor is already here, on voice.',
    chat: 'Your advisor is already here, in the chat.',
  },
} as const;

// ---------------------------------------------------------------------------
// ThankYouScreen
// ---------------------------------------------------------------------------

export const thankYouCopy = {
  heading: 'Thank You',
  bodyTemplate: (orgName: string) =>
    `We hope you enjoyed your virtual consultation with ${orgName}. Your experience matters to us.`,
  cta: 'Share Your Feedback',
  feedbackSuccess: {
    primary: 'Thank you for your feedback!',
    secondary: 'Your input helps us improve our service.',
  },
} as const;

// ---------------------------------------------------------------------------
// TransitionScreen — format-aware permission-prompt copy
// (chat copy conveys that access is optional; only needed if switching modes)
// ---------------------------------------------------------------------------

export const PROMPT_COPY: Record<CommunicationMode, { heading: string; body: string }> = {
  video: {
    heading: 'Camera & Microphone Access',
    body: 'For your face-to-face consultation, we need access to your camera and microphone. Your browser will ask for permission. Please select Allow to continue.',
  },
  audio: {
    heading: 'Microphone Access',
    body: 'For your voice consultation, we need access to your microphone. Your camera will stay off. Your browser will ask for permission. Please select Allow to continue.',
  },
  chat: {
    heading: 'Optional Camera & Microphone Access',
    body: 'You chose chat — no camera or microphone is required. You may grant access now if you think you might switch to a voice or video call later. You can safely continue to chat without granting.',
  },
};
